import { Modal } from "../../Components/Dialog/Modal";
import { activateTab, TAB_NAV_EVENT } from "../Tabbed";
import { AbstractFormElement } from "../../Components/Form/Abstract";
import { InputText } from "../../Components/Form/Input";
import { Dropdown } from "../../Components/Form/Dropdown";
import {
    IFinancingOptions,
    ILeasingOptionEbike,
    ILeasingOptions,
    ILeasingRates,
    IMonthlyRates
} from "../../Interfaces/RateCalculator";
import {
    FINANCING_MONTHLY_RATE_DEFAULT,
    FINANCING_OPTIONS_AVAILABLE_TERMS,
    LEASING_OPTIONS_AVAILABLE_TERMS,
    MUTATION_OBSERVER_OPTIONS,
    SELECTORS
} from "./Constants";
import { MoneyFormatter } from "../../Utils/Money";

class RateCalculator {
    private readonly rateCalculator: HTMLElement;
    private readonly button: HTMLButtonElement;
    private readonly currencySymbol: string;
    private readonly conditionNew: boolean;
    private readonly financingOptions: IFinancingOptions;
    private readonly financingMonthlyRates: IMonthlyRates;
    private readonly financingMonthlyRateDefault: number;
    private readonly financingAvailableTerms: number[];
    private readonly leasingOptions?: ILeasingOptions;
    private readonly leasingOptionsEbike?: ILeasingOptionEbike[];
    private readonly leasingMonthlyRates: ILeasingRates;
    private readonly leasingAvailableTerms: number[];
    private text: string;
    private durationFinancing: number;
    private durationLeasing: number;
    private grossSalary: number;
    private downPayment: number;
    private readonly maxDurationFinancing: number;
    private readonly maxDurationLeasing: number;
    private readonly maxGrossSalaryLeasing: number;
    private tabbed: HTMLElement;
    private tabFinancing: HTMLElement;
    private tabLeasing: HTMLElement;
    private financingDownPaymentInput: InputText;
    private financingDownPaymentInputPreviousValue: string;
    private financingDownPaymentInputDeletedIndex: string = null;
    private financingDurationInput: Dropdown;
    private leasingDurationInput: Dropdown;
    private leasingGrossSalaryInput: Dropdown;
    private changeHandler: () => void;
    private downPaymentHandler: () => void;
    private mutationHandler: (mutations: MutationRecord[], observer: MutationObserver) => void;
    private mutationObserver: MutationObserver;

    constructor(rateCalculator: HTMLElement) {
        this.rateCalculator = rateCalculator;
        this.currencySymbol = this.rateCalculator.dataset.currencySymbol;

        this.financingOptions = JSON.parse(this.rateCalculator.dataset.financing);
        this.financingMonthlyRateDefault =
            +this.rateCalculator.dataset.monthlyRateDefault || FINANCING_MONTHLY_RATE_DEFAULT;
        this.financingMonthlyRates = {
            new_bike: this.financingOptions?.rate_new_vehicles,
            used_bike: this.financingOptions?.rate_old_vehicles,
            rate_bikes: this.financingOptions?.rate_bikes
        };
        this.financingAvailableTerms = this.financingOptions?.available_terms || FINANCING_OPTIONS_AVAILABLE_TERMS;
        this.maxDurationFinancing = Math.max(...this.financingAvailableTerms);

        try {
            if (this.shop === "lim-e") {
                this.leasingOptionsEbike = JSON.parse(this.rateCalculator.dataset.leasing);

                const gross_salary_values = this.leasingOptionsEbike?.map(
                    (option) => +option.gross_salary_customer.amount
                );
                this.maxGrossSalaryLeasing = Math.max(...gross_salary_values);
                this.grossSalary = this.maxGrossSalaryLeasing;
            } else {
                this.leasingOptions = JSON.parse(this.rateCalculator.dataset.leasing);
                this.leasingMonthlyRates = {
                    leasing_rate_30: this.leasingOptions?.leasing_rate_30,
                    leasing_rate_60: this.leasingOptions?.leasing_rate_60
                };
                this.leasingAvailableTerms = this.leasingOptions?.available_terms || LEASING_OPTIONS_AVAILABLE_TERMS;
                this.maxDurationLeasing = Math.max(...this.leasingAvailableTerms);
            }
        } catch (e) {
            if (this.rateCalculator.dataset.leasing) {
                console.error(e);
            }
        }

        if (this.isModule) {
            this.button = this.rateCalculator.querySelector<HTMLButtonElement>(SELECTORS.button);
        }
        this.text = this.rateCalculator.dataset.text;
        this.conditionNew = this.rateCalculator.dataset.conditionNew === "true";
        this.durationFinancing = this.maxDurationFinancing;
        this.durationLeasing = this.maxDurationLeasing;
        this.grossSalary = this.maxGrossSalaryLeasing;

        this.setFinancingFromValue().setLeasingFromValue().initListeners().addListeners().initMutationObserver();
    }

    initListeners() {
        this.mutationHandler = this.handleMutation.bind(this);

        return this;
    }

    addListeners() {
        if (this.isModule) {
            this.button.addEventListener("click", this.openCalculator.bind(this));
        }
        return this;
    }

    initMutationObserver() {
        this.mutationObserver = new MutationObserver(this.mutationHandler);
        this.mutationObserver.observe(this.rateCalculator, MUTATION_OBSERVER_OPTIONS);
        return this;
    }

    private handleMutation(mutationList: MutationRecord[]) {
        mutationList.forEach((mutation) => {
            if (mutation.type === "attributes") {
                if (mutation.attributeName === "data-price") {
                    this.setFinancingFromValue().setLeasingFromValue();
                }
            }
        });
    }

    private setFinancingFromValue() {
        this.downPayment = this.price / 2;
        if (this.isModule) {
            const financingFrom = this.rateCalculator.querySelector<HTMLElement>(SELECTORS.financingFrom);
            financingFrom.innerHTML = this.formatNumber(this.rate);
        } else {
            this.rateCalculator.innerHTML = this.text.replace("{{ amount }}", Math.round(this.rate).toString());
        }

        return this;
    }

    private setLeasingFromValue() {
        if (this.leasingOptions || this.leasingOptionsEbike) {
            const durationOrSalary = this.maxDurationLeasing || this.maxGrossSalaryLeasing;
            this.handleLeasingRateChange(durationOrSalary);
        }
        return this;
    }

    private openCalculator() {
        const modal = new Modal("rate-calculator-modal");
        modal.render();
        this.initTabbed().initTabbedListeners().setFinancingTabValues();

        if (this.leasingOptions || this.leasingOptionsEbike) {
            this.setLeasingTabValues();
        }
    }

    private initTabbed() {
        this.tabbed = document.querySelector<HTMLElement>(SELECTORS.tabbed);
        this.tabFinancing = this.tabbed?.querySelector<HTMLElement>(SELECTORS.tabFinancing);
        this.tabLeasing = this.tabbed?.querySelector<HTMLElement>(SELECTORS.tabLeasing);
        this.financingDownPaymentInput = this.tabFinancing?.querySelector<InputText>("#down-payment");
        this.financingDurationInput = this.tabFinancing?.querySelector<Dropdown>("#duration");
        if (this.shop === "lim-e") {
            this.leasingGrossSalaryInput = this.tabLeasing?.querySelector<Dropdown>("#gross-salary");
        } else {
            this.leasingDurationInput = this.tabLeasing?.querySelector<Dropdown>("#duration");
        }

        if (this.tabbed) {
            const labels = this.tabbed.querySelectorAll(":scope > nav > div > span") as NodeListOf<HTMLSpanElement>;
            const tab = labels[0].dataset.tab;
            if (tab) {
                activateTab(tab);
            }

            labels.forEach((label) => {
                label.addEventListener("click", () => {
                    const id = label.dataset.tab;
                    const tab = label.closest(SELECTORS.tabbed).querySelector<HTMLElement>(`[id*="tab-${id}"]`);
                    activateTab(id);
                    tab.dispatchEvent(new CustomEvent(TAB_NAV_EVENT, { detail: label }));
                });
            });
        }
        return this;
    }

    private setFinancingTabValues() {
        const purchasePrice = this.tabFinancing.querySelector<HTMLElement>(".purchase > .price");
        purchasePrice.innerHTML = this.formatNumber(this.price);
        const interest = this.tabFinancing.querySelector<HTMLElement>(".interest > .price");
        interest.innerHTML = this.monthlyInterestRate.toString() + " %";

        if (this.financingDownPaymentInput) {
            this.financingDownPaymentInput.value = this.formatNumber(this.downPayment);
            this.financingDownPaymentInput.dispatchEvent(new Event("input"));
        }

        if (this.financingDurationInput) {
            this.financingDurationInput.value = this.durationFinancing.toString();
            this.financingDurationInput.dispatchEvent(new Event("change"));
        }

        return this;
    }

    private setLeasingTabValues() {
        const purchasePrice = this.tabLeasing.querySelector<HTMLElement>(".purchase > .price");
        purchasePrice.innerHTML = this.formatNumber(this.price);
        const durationOrSalary = this.durationLeasing || this.grossSalary;
        if (this.leasingDurationInput) {
            this.leasingDurationInput.value = durationOrSalary.toString();
            this.leasingDurationInput.dispatchEvent(new Event("change"));
        }
        if (this.leasingGrossSalaryInput) {
            this.leasingGrossSalaryInput.value = durationOrSalary.toString();
            this.leasingGrossSalaryInput.dispatchEvent(new Event("change"));
        }
        this.handleLeasingRateChange(durationOrSalary);

        return this;
    }

    private initTabbedListeners() {
        this.changeHandler = this.handleChange.bind(this);
        this.downPaymentHandler = this.handleDownPayment.bind(this);

        this.financingDownPaymentInput.addEventListener("click", this.downPaymentHandler);
        this.financingDownPaymentInput.addEventListener("input", this.downPaymentHandler);
        this.financingDownPaymentInput.addEventListener("keyup", this.downPaymentHandler);
        this.financingDownPaymentInput.addEventListener("keydown", this.downPaymentHandler);
        this.financingDurationInput.addEventListener("change", this.changeHandler);
        this.leasingDurationInput?.addEventListener("change", this.changeHandler);
        this.leasingGrossSalaryInput?.addEventListener("change", this.changeHandler);

        return this;
    }

    handleChange(event: Event) {
        switch (event.target) {
            case this.financingDurationInput:
                this.durationFinancing = +this.financingDurationInput.value;
                this.handleMonthlyRateChange();
                break;
            case this.leasingDurationInput:
                this.durationLeasing = +this.leasingDurationInput.value;
                this.handleLeasingRateChange(this.durationLeasing);
                break;
            case this.leasingGrossSalaryInput:
                this.grossSalary = +this.leasingGrossSalaryInput.value;
                this.handleLeasingRateChange(this.grossSalary);
                break;
        }
    }

    handleDownPayment(e: Event) {
        const target = e.currentTarget as InputText;
        let caretPosition = target.selectionStart;
        let previousCaretPosition = caretPosition;

        if (e instanceof KeyboardEvent) {
            const valueAsNumber = +this.financingDownPaymentInput.value.slice(0, -2).replace(".", "");

            switch (e.code) {
                case "ArrowUp":
                    this.financingDownPaymentInput.value = (valueAsNumber + 100).toString();
                    caretPosition = this.financingDownPaymentInput.value.length + 1;
                    break;
                case "ArrowDown":
                    this.financingDownPaymentInput.value = (valueAsNumber - 100).toString();
                    break;
            }

            if (e.type === "keydown") {
                this.financingDownPaymentInputPreviousValue = target.value.slice(0, -2);
                if (e.code === "Backspace") {
                    previousCaretPosition = target.selectionStart;
                    this.financingDownPaymentInputDeletedIndex = target.value
                        .substring(0, caretPosition - 1)
                        .length.toString();
                }
            }
        }

        this.formatDownPaymentInput(this.financingDownPaymentInput);
        const value = this.financingDownPaymentInput.value.slice(0, -2);

        if (caretPosition > value.length) {
            this.financingDownPaymentInput.setSelectionRange(value.length, value.length);
        } else if (e.type === "input" && value.length > 3 && this.financingDownPaymentInputDeletedIndex) {
            this.financingDownPaymentInput.setSelectionRange(previousCaretPosition + 1, previousCaretPosition + 1);
            this.financingDownPaymentInputDeletedIndex = null;
        }

        this.handleMonthlyRateChange();
    }

    handleMonthlyRateChange() {
        const monthlyRateTab = this.tabFinancing.querySelector<HTMLElement>(".rate > .price");
        const monthlyRateDetail = this.rateCalculator.querySelector<HTMLElement>(SELECTORS.financingFrom);
        monthlyRateTab.innerHTML = this.formatNumberWithDecimals(this.rate);
        monthlyRateDetail.innerHTML = this.formatNumber(this.rate);
    }

    handleLeasingRateChange(durationOrSalary: number) {
        const leasingFromElementDetail = this.rateCalculator.querySelector<HTMLElement>(SELECTORS.leasingFrom);
        const leasingFromElementTab = this.tabLeasing?.querySelector<HTMLElement>(".rate > .price");
        let leasingValueDetail;
        let leasingValueTab;
        if (this.shop === "lim-e") {
            leasingValueTab = this.formatNumberWithDecimals(this.leasingRateEbike * 100);
            leasingValueDetail = this.formatNumber(this.leasingRateEbike * 100);
        } else {
            const durationKey = `leasing_rate_${durationOrSalary}` as keyof ILeasingRates;
            leasingValueTab = this.formatNumberWithDecimals(this.leasingMonthlyRates[durationKey] * 100);
            leasingValueDetail = this.formatNumber(this.leasingMonthlyRates[durationKey] * 100);
        }

        if (leasingFromElementDetail) leasingFromElementDetail.innerHTML = leasingValueDetail;
        if (leasingFromElementTab) leasingFromElementTab.innerHTML = leasingValueTab;
    }

    private formatDownPaymentInput(element: AbstractFormElement) {
        if (element.value) {
            const trimmedValue = this.trimNumber(element.value);
            const value = trimmedValue <= this.price ? this.formatNumber(trimmedValue) : this.formatNumber(this.price);

            this.downPayment = trimmedValue <= this.price ? trimmedValue : this.price;
            element.value = value;
            element.validate();
        }
    }

    private formatNumber(value: number): string {
        return new MoneyFormatter().noDecimals().format(value);
    }

    private formatNumberWithDecimals(value: number): string {
        return new MoneyFormatter().format(value);
    }

    private trimNumber(value: string): number {
        return +value.replace(/\D/g, "") * 100;
    }

    get price(): number {
        return +this.rateCalculator.dataset.price;
    }

    /**
     * Round price to the closest a thousand number
     */
    get roundedPrice(): number {
        // Price is in Shopify format with cents at the end
        return Math.round(this.price / 100 / 1000) * 1000;
    }

    set price(value: number) {
        this.rateCalculator.dataset.price = new MoneyFormatter().stripSymbol().format(value);
    }

    get monthlyInterestRate(): number {
        return (
            this.financingMonthlyRates[this.conditionNew ? "new_bike" : "used_bike"] ||
            this.financingMonthlyRates["rate_bikes"] ||
            this.financingMonthlyRateDefault
        );
    }

    get interest(): number {
        return this.monthlyInterestRate / 100;
    }

    get interestFactor(): number {
        return Math.pow(1 + this.interest, 1 / 12) - 1;
    }

    get principal(): number {
        return this.price - this.downPayment;
    }

    get rate(): number {
        const P = this.principal;
        const p = this.interestFactor;
        const n = this.durationFinancing;
        const rate = P * p * (Math.pow(1 + p, n) / (Math.pow(1 + p, n) - 1));
        return +rate.toFixed(2);
    }

    get leasingRateEbike(): number {
        return this.closestRateByProductPrice.leasing_rate;
    }

    get ratesBySalary(): ILeasingOptionEbike[] {
        return this.leasingOptionsEbike.filter((option) => +option.gross_salary_customer.amount === this.grossSalary);
    }

    get closestRateByProductPrice() {
        return this.ratesBySalary.reduce((prev, curr) => {
            return Math.abs(+curr.price_ebike.amount - this.roundedPrice) <
                Math.abs(+prev.price_ebike.amount - this.roundedPrice)
                ? curr
                : prev;
        });
    }

    get shop(): string {
        return this.rateCalculator.dataset.shop;
    }

    get isModule(): boolean {
        return this.rateCalculator.dataset.module === "true";
    }
}

const rateCalculatorContainer = document.querySelector<HTMLElement>(SELECTORS.container);

if (rateCalculatorContainer) {
    new RateCalculator(rateCalculatorContainer);
}

const rateCalculatorInitializer = document.querySelectorAll(SELECTORS.initializer) as NodeListOf<HTMLElement>;
if (rateCalculatorInitializer.length) {
    rateCalculatorInitializer.forEach((element) => {
        new RateCalculator(element);
    });
}
